import React, { useRef, useState } from "react";
import { api, imageUrl } from "@/lib/api";
import { toast } from "sonner";
import { useColours } from "@/contexts/ColoursContext";
import { Upload, X, ChevronDown, ImageIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * Picks a single image to use as a material's thumbnail. Two paths:
 *   1. Upload a fresh image → POST /api/upload returns a path
 *   2. Reuse the swatch image from a colour the user has already created.
 *
 * Both write the same `thumbnail_image` string. Showing a 40x40 preview and
 * a × button to clear. The dropdown is hidden if no colour has a swatch image
 * yet, so the picker degrades gracefully.
 */
const MaterialThumbnailPicker = ({
  value,
  onChange,
  busy = false,
  testidName = "",
}) => {
  const { colours } = useColours();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const swatchable = colours.filter((c) => c.swatch_image);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.path);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const disabled = busy || uploading;

  return (
    <div className="flex items-center gap-2">
      <div
        data-testid={`material-thumb-preview-${testidName}`}
        className="relative h-10 w-10 border-2 border-black rounded-md overflow-hidden bg-[#F4F8FB] flex items-center justify-center"
      >
        {value ? (
          <>
            <img
              src={imageUrl(value)}
              alt=""
              className="w-full h-full object-cover"
            />
            <button
              type="button"
              data-testid={`material-thumb-clear-${testidName}`}
              onClick={() => onChange(null)}
              disabled={disabled}
              className="absolute -top-1 -right-1 bg-white border border-black rounded-full p-0.5"
              aria-label="clear thumbnail"
              title="Remove thumbnail"
            >
              <X size={10} strokeWidth={2.5} />
            </button>
          </>
        ) : (
          <ImageIcon size={16} strokeWidth={2.5} className="text-[#001740]/40" />
        )}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid={`material-thumb-upload-input-${testidName}`}
        onChange={(e) => upload(e.target.files?.[0])}
      />
      <button
        type="button"
        data-testid={`material-thumb-upload-${testidName}`}
        onClick={() => fileRef.current?.click()}
        disabled={disabled}
        className="nb-btn bg-white p-2"
        title={value ? "Replace with a fresh upload" : "Upload an image"}
      >
        <Upload size={14} strokeWidth={2.5} />
      </button>

      {swatchable.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid={`material-thumb-pick-${testidName}`}
              disabled={disabled}
              className="nb-btn bg-white px-2 py-2 flex items-center gap-1 text-xs"
              title="Reuse one of your colour swatches"
            >
              <span>Pick a swatch</span>
              <ChevronDown size={12} strokeWidth={2.5} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            data-testid={`material-thumb-pick-menu-${testidName}`}
            className="border-2 border-black rounded-xl shadow-[4px_4px_0_#001740] bg-white max-h-64 overflow-y-auto p-1.5 min-w-[10rem]"
          >
            {swatchable.map((c) => (
              <DropdownMenuItem
                key={c.id}
                data-testid={`material-thumb-pick-${testidName}-${c.name}`}
                onClick={() => onChange(c.swatch_image)}
                className="flex items-center gap-2 px-2 py-1.5 text-sm font-semibold cursor-pointer rounded-md focus:bg-[#F1E7FA] focus:text-[#5B3A99]"
              >
                <img
                  src={imageUrl(c.swatch_image)}
                  alt=""
                  className="w-6 h-6 rounded-md border border-black/40 object-cover"
                />
                {c.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};

export default MaterialThumbnailPicker;
